import type { RequiredGap } from '@seen/core/observation-logic/noteCapture'
import { Button } from '@seen/ui'
import type { JSX } from 'react'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'

export interface RequiredAnswersDialogProps {
  gaps: readonly RequiredGap[]
  open: boolean
  // What the note is about to do: "Sign" or "Submit".
  action: 'Sign' | 'Submit'
  // Go back to the note to answer them.
  onAnswer: () => void
  // Go ahead without them.
  onOverride: () => void
}

// Says which required questions a note shows with no answer, before it is
// signed or submitted. Going ahead anyway stays possible, for a template that
// asks for something that does not apply.
export function RequiredAnswersDialog({
  gaps,
  open,
  action,
  onAnswer,
  onOverride,
}: RequiredAnswersDialogProps): JSX.Element {
  return (
    <Dialog open={open} onOpenChange={(next) => !next && onAnswer()}>
      <DialogDisplay>
        <DialogHeader asChild>
          <DialogTitle>
            <p>
              {gaps.length === 1
                ? '1 required question has no answer'
                : `${gaps.length} required questions have no answer`}
            </p>
          </DialogTitle>
        </DialogHeader>
        <DialogContent className="py-0">
          <DialogDescription>
            The template marks these as required.
          </DialogDescription>
          <ul className="mt-2 list-disc pl-5 text-sm">
            {gaps.map((gap) => (
              <li key={gap.id}>
                {gap.label}
                {gap.unanswered ? (
                  <span className="text-muted-foreground">
                    {' '}
                    · {gap.unanswered} unanswered
                  </span>
                ) : null}
              </li>
            ))}
          </ul>
        </DialogContent>
        <DialogFooter className="mt-2 sm:flex-row-reverse sm:justify-start">
          <Button onClick={onAnswer}>Answer them</Button>
          <Button variant="outline" onClick={onOverride}>
            {action} anyway
          </Button>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}
